// ============================================================
// document-flatten : mise à plat et nettoyage d'un document scanné
// ============================================================
// Complète document-scanner.ts (qui trouve OÙ est le document) en traitant ce
// qu'on en fait ensuite : le remettre à plat, retirer le fond, blanchir le
// papier sans pâlir l'encre.
//
// Trois enseignements de la mise au point, à ne pas défaire :
//
//  1. Un modèle de saillance (u2netp) n'est pas un détecteur de document : posez
//     un ticket sur un clavier et il détoure le clavier. Scanic, lui, est
//     entraîné à trouver les quatre coins d'un DOCUMENT, en une centaine de ms.
//
//  2. Une géométrie à quatre coins ne peut pas corriger la courbure du papier :
//     elle force les bords haut et bas à devenir droits, ce qui tasse le texte.
//     UVDoc (ETH Zurich, Apache 2.0) prédit une grille de déformation et fait
//     tomber l'ondulation des lignes de 14-29 px à 1-4 px sur nos photos de
//     recette. Il n'est en revanche pas fait pour une capture d'écran : on
//     compare donc son rendu à celui de la perspective et on garde le meilleur.
//
//  3. Tout seuil calculé sur l'image entière se trompe dès que l'éclairage est
//     inégal. Le bas d'un ticket peut être plus sombre que le fond du haut :
//     un seuil global efface alors le document en croyant retirer le décor.
//     Tous les seuils de ce fichier sont calculés ligne par ligne.
// ============================================================

// Le type de session et la déclaration globale de `window.ort` viennent de
// document-scanner : un seul endroit décrit le runtime ONNX.
import type { Corner, Quad, DocModelSession } from './document-scanner';

// ── Scanic : détection des quatre coins d'un document ──────────

interface ScanicModule {
  scanDocument: (el: HTMLCanvasElement | HTMLImageElement, opts?: Record<string, unknown>) => Promise<{
    success?: boolean;
    score?: number;
    corners?: { topLeft: Corner; topRight: Corner; bottomRight: Corner; bottomLeft: Corner };
  }>;
}

const SCANIC_URL = 'https://cdn.jsdelivr.net/npm/scanic/+esm';
let scanicPromise: Promise<ScanicModule> | null = null;

export function chargerScanic(): Promise<ScanicModule> {
  scanicPromise ??= (import(/* webpackIgnore: true */ SCANIC_URL) as Promise<ScanicModule>)
    .catch((e) => { scanicPromise = null; throw e; });
  return scanicPromise;
}

export async function detecterQuadScanic(el: HTMLCanvasElement | HTMLImageElement): Promise<Quad | null> {
  const m = await chargerScanic();
  const r = await m.scanDocument(el, { mode: 'detect', detector: 'ml' });
  if (!r?.success || !r.corners) return null;
  const c = r.corners;
  const q: Quad = { tl: c.topLeft, tr: c.topRight, br: c.bottomRight, bl: c.bottomLeft };
  for (const p of [q.tl, q.tr, q.br, q.bl]) {
    if (!p || !isFinite(p.x) || !isFinite(p.y)) return null;
  }
  return q;
}

// ── UVDoc : mise à plat par grille prédite ─────────────────────

const UV_W = 496, UV_H = 720, UV_GW = 31, UV_GH = 45;
const MAX_TRAVAIL = 2400;   // taille de travail éprouvée en recette
const UV_MODELE = '/models/UVDoc_grid.onnx';
const UV_POIDS = '/models/UVDoc_grid.onnx.data';

let uvPromise: Promise<DocModelSession> | null = null;
let uvMoteur = '';

export function moteurUVDoc(): string { return uvMoteur; }

export function chargerUVDoc(): Promise<DocModelSession> {
  if (uvPromise) return uvPromise;
  uvPromise = (async () => {
    const ort = window.ort;
    if (!ort) throw new Error('runtime absent');
    const opts = { externalData: [{ path: 'UVDoc_grid.onnx.data', data: UV_POIDS }] };
    try {
      const s = await ort.InferenceSession.create(UV_MODELE, { ...opts, executionProviders: ['webgpu'] });
      uvMoteur = 'gpu';
      return s;
    } catch {
      // Sans WebGPU l'inférence passe de 30 ms à 5 s : utilisable, mais il faut
      // le savoir pour ne pas laisser l'utilisateur devant un écran figé.
      const s = await ort.InferenceSession.create(UV_MODELE, { ...opts, executionProviders: ['wasm'] });
      uvMoteur = 'cpu';
      return s;
    }
  })().catch((e) => { uvPromise = null; throw e; });
  return uvPromise;
}

function echantillonner(src: Uint8ClampedArray, sw: number, sh: number, x: number, y: number, dst: Uint8ClampedArray, di: number): void {
  if (x < 0) x = 0; else if (x > sw - 1) x = sw - 1;
  if (y < 0) y = 0; else if (y > sh - 1) y = sh - 1;
  const x0 = x | 0, y0 = y | 0;
  const x1 = Math.min(sw - 1, x0 + 1), y1 = Math.min(sh - 1, y0 + 1);
  const fx = x - x0, fy = y - y0;
  const i00 = (y0 * sw + x0) * 4, i10 = (y0 * sw + x1) * 4;
  const i01 = (y1 * sw + x0) * 4, i11 = (y1 * sw + x1) * 4;
  const w00 = (1 - fx) * (1 - fy), w10 = fx * (1 - fy), w01 = (1 - fx) * fy, w11 = fx * fy;
  for (let k = 0; k < 3; k++) {
    dst[di + k] = src[i00 + k] * w00 + src[i10 + k] * w10 + src[i01 + k] * w01 + src[i11 + k] * w11;
  }
  dst[di + 3] = 255;
}

function canvasDepuis(d: ImageData): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = d.width; c.height = d.height;
  c.getContext('2d')!.putImageData(d, 0, 0);
  return c;
}

function ctx2d(c: HTMLCanvasElement): CanvasRenderingContext2D {
  return c.getContext('2d', { willReadFrequently: true })!;
}

// Remet le document à plat. La sortie est le document seul : les bords de la
// grille prédite sont les bords du document.
export async function aplatirUVDoc(src: HTMLCanvasElement, maxSortie = 2000): Promise<{ canvas: HTMLCanvasElement; ms: number } | null> {
  const sess = await chargerUVDoc();
  const ort = window.ort;
  if (!ort) return null;
  const W = src.width, H = src.height;

  const petit = document.createElement('canvas');
  petit.width = UV_W; petit.height = UV_H;
  ctx2d(petit).drawImage(src, 0, 0, W, H, 0, 0, UV_W, UV_H);
  const d = ctx2d(petit).getImageData(0, 0, UV_W, UV_H).data;
  const plan = UV_W * UV_H, f = new Float32Array(3 * plan);
  for (let p = 0, j = 0; p < plan; p++, j += 4) {
    f[p] = d[j] / 255; f[plan + p] = d[j + 1] / 255; f[2 * plan + p] = d[j + 2] / 255;
  }

  const t0 = performance.now();
  const out = await sess.run({ image: new ort.Tensor('float32', f, [1, 3, UV_H, UV_W]) });
  const ms = Math.round(performance.now() - t0);
  const brut = out.grid_2d?.data;
  if (!brut) return null;
  const g = brut instanceof Float32Array ? brut : Float32Array.from(brut as ArrayLike<number>);
  const gx = (i: number, j: number) => g[i * UV_GW + j];
  const gy = (i: number, j: number) => g[UV_GH * UV_GW + i * UV_GW + j];
  const enPx = (sx: number, sy: number): [number, number] => [((sx + 1) / 2) * (W - 1), ((sy + 1) / 2) * (H - 1)];

  // Taille de sortie = longueur réelle des bords du document dans la photo,
  // pour ne ni étirer ni tasser le contenu.
  let lh = 0, lv = 0;
  for (let j = 1; j < UV_GW; j++) {
    const a = enPx(gx(0, j - 1), gy(0, j - 1)), b = enPx(gx(0, j), gy(0, j));
    const a2 = enPx(gx(UV_GH - 1, j - 1), gy(UV_GH - 1, j - 1)), b2 = enPx(gx(UV_GH - 1, j), gy(UV_GH - 1, j));
    lh += (Math.hypot(b[0] - a[0], b[1] - a[1]) + Math.hypot(b2[0] - a2[0], b2[1] - a2[1])) / 2;
  }
  for (let i = 1; i < UV_GH; i++) {
    const a = enPx(gx(i - 1, 0), gy(i - 1, 0)), b = enPx(gx(i, 0), gy(i, 0));
    const a2 = enPx(gx(i - 1, UV_GW - 1), gy(i - 1, UV_GW - 1)), b2 = enPx(gx(i, UV_GW - 1), gy(i, UV_GW - 1));
    lv += (Math.hypot(b[0] - a[0], b[1] - a[1]) + Math.hypot(b2[0] - a2[0], b2[1] - a2[1])) / 2;
  }
  const ech = Math.min(1, maxSortie / Math.max(lh, lv, 1));
  const Wo = Math.max(40, Math.round(lh * ech)), Ho = Math.max(40, Math.round(lv * ech));

  const sdata = ctx2d(src).getImageData(0, 0, W, H).data;
  const img = new ImageData(Wo, Ho), o = img.data;
  for (let r = 0; r < Ho; r++) {
    const v = (r / (Ho - 1)) * (UV_GH - 1), i0 = Math.min(UV_GH - 2, v | 0), fv = v - i0;
    for (let cc = 0; cc < Wo; cc++) {
      const u = (cc / (Wo - 1)) * (UV_GW - 1), j0 = Math.min(UV_GW - 2, u | 0), fu = u - j0;
      const sx = (1 - fv) * ((1 - fu) * gx(i0, j0) + fu * gx(i0, j0 + 1)) + fv * ((1 - fu) * gx(i0 + 1, j0) + fu * gx(i0 + 1, j0 + 1));
      const sy = (1 - fv) * ((1 - fu) * gy(i0, j0) + fu * gy(i0, j0 + 1)) + fv * ((1 - fu) * gy(i0 + 1, j0) + fu * gy(i0 + 1, j0 + 1));
      echantillonner(sdata, W, H, ((sx + 1) / 2) * (W - 1), ((sy + 1) / 2) * (H - 1), o, (r * Wo + cc) * 4);
    }
  }
  return { canvas: canvasDepuis(img), ms };
}

// Vue recadrée où le document occupe `cible` du cadre. Trop serré, le modèle
// manque de contexte pour placer les bords ; trop large, il se trompe d'objet.
export function cadrerPour(src: HTMLImageElement | HTMLCanvasElement, quad: Quad, cible = 0.88): HTMLCanvasElement | null {
  const W = src instanceof HTMLImageElement ? src.naturalWidth : src.width;
  const H = src instanceof HTMLImageElement ? src.naturalHeight : src.height;
  const xs = [quad.tl.x, quad.tr.x, quad.br.x, quad.bl.x];
  const ys = [quad.tl.y, quad.tr.y, quad.br.y, quad.bl.y];
  const x0 = Math.min(...xs), x1 = Math.max(...xs), y0 = Math.min(...ys), y1 = Math.max(...ys);
  const dw = x1 - x0, dh = y1 - y0;
  if (dw < 20 || dh < 20) return null;
  const m = Math.max(0, (1 / cible - 1) / 2);
  const bx0 = Math.max(0, x0 - dw * m), by0 = Math.max(0, y0 - dh * m);
  const bx1 = Math.min(W, x1 + dw * m), by1 = Math.min(H, y1 + dh * m);
  // Borne de travail : au-delà, le calcul par pixel ralentit le téléphone sans
  // rien apporter, puisque l'image finale est de toute façon ramenée à 2000 px.
  const largeur = Math.max(20, Math.round(bx1 - bx0));
  const hauteur = Math.max(20, Math.round(by1 - by0));
  const ech = Math.min(1, MAX_TRAVAIL / Math.max(largeur, hauteur));
  const c = document.createElement('canvas');
  c.width = Math.max(20, Math.round(largeur * ech));
  c.height = Math.max(20, Math.round(hauteur * ech));
  ctx2d(c).drawImage(src, bx0, by0, bx1 - bx0, by1 - by0, 0, 0, c.width, c.height);
  return c;
}

// ── Mesures d'arbitrage ────────────────────────────────────────

function luminances(cv: HTMLCanvasElement): Float32Array {
  const w = cv.width, h = cv.height;
  const d = ctx2d(cv).getImageData(0, 0, w, h).data;
  const L = new Float32Array(w * h);
  for (let i = 0, j = 0; i < w * h; i++, j += 4) L[i] = 0.299 * d[j] + 0.587 * d[j + 1] + 0.114 * d[j + 2];
  return L;
}

function medianeDe(v: number[]): number {
  if (!v.length) return 1;
  v.sort((a, b) => a - b);
  return v[v.length >> 1];
}

// Ondulation des lignes de texte, en pixels. C'est ce que l'oeil voit comme
// "document tordu", et le seul juge valable pour départager deux mises à plat.
// Recherche bornée à 18 px pour ne pas confondre un décalage et un interligne.
export function ondulationLignes(cv: HTMLCanvasElement): number {
  const w = cv.width, h = cv.height;
  if (w < 60 || h < 60) return 0;
  const L = luminances(cv);
  const med = medianeDe(Array.from(L.filter((_, i) => i % 7 === 0)));
  const K = 7, D = 18;
  const bandes: Float32Array[] = [];
  for (let k = 0; k < K; k++) {
    const x0 = Math.round((k * (w - w / K)) / (K - 1)), x1 = Math.min(w, x0 + Math.round(w / K));
    const p = new Float32Array(h);
    for (let y = 0; y < h; y++) {
      let a = 0;
      for (let x = x0; x < x1; x += 2) { const v = med - L[y * w + x]; if (v > 0) a += v; }
      p[y] = a;
    }
    bandes.push(p);
  }
  const ref = bandes[K >> 1];
  let nRef = 0;
  for (let y = 0; y < h; y++) nRef += ref[y] * ref[y];
  nRef = Math.sqrt(nRef);
  if (nRef < 1e-6) return 0;
  const decs: number[] = [];
  for (const b of bandes) {
    let nb = 0;
    for (let y = 0; y < h; y++) nb += b[y] * b[y];
    nb = Math.sqrt(nb);
    if (nb < 1e-6) continue;
    let best = -2, bs = 0;
    for (let sh = -D; sh <= D; sh++) {
      let acc = 0;
      for (let y = Math.max(0, -sh); y < Math.min(h, h - sh); y++) acc += ref[y] * b[y + sh];
      const v = acc / (nRef * nb);
      if (v > best) { best = v; bs = sh; }
    }
    if (best > 0.45) decs.push(bs);
  }
  return decs.length < 2 ? 0 : Math.max(...decs) - Math.min(...decs);
}

// Fond restant sur le pourtour, en pixels moyens ramenés à une échelle commune.
export function fondAuPourtour(cv: HTMLCanvasElement): number {
  const w = cv.width, h = cv.height;
  if (w < 30 || h < 30) return 0;
  const L = luminances(cv);
  const papL: number[] = [], papC: number[] = [];
  for (let y = 0; y < h; y++) {
    const v: number[] = [];
    for (let x = (w * 0.3) | 0; x < w * 0.7; x += 2) v.push(L[y * w + x]);
    papL.push(medianeDe(v));
  }
  for (let x = 0; x < w; x++) {
    const v: number[] = [];
    for (let y = (h * 0.3) | 0; y < h * 0.7; y += 2) v.push(L[y * w + x]);
    papC.push(medianeDe(v));
  }
  const lim = Math.max(4, Math.round(Math.min(w, h) * 0.2));
  const cote = (cnt: number, lire: (i: number, c: number) => number, pap: number[]): number => {
    let t = 0;
    for (let i = 0; i < cnt; i++) {
      const seuil = Math.max(15, pap[i] * 0.72);
      let c = 0;
      while (c < lim && lire(i, c) < seuil) c++;
      t += c;
    }
    return t / cnt;
  };
  const ech = 360 / Math.max(1, Math.min(w, h));
  return ech * Math.max(
    cote(h, (y, c) => L[y * w + c], papL),
    cote(h, (y, c) => L[y * w + (w - 1 - c)], papL),
    cote(w, (x, c) => L[c * w + x], papC),
    cote(w, (x, c) => L[(h - 1 - c) * w + x], papC),
  );
}

// Part d'encre collée aux quatre bords, en %. Sur un document entier il y a
// toujours une marge de papier : une valeur élevée signifie qu'on a coupé dans
// le texte. À mesurer APRÈS blanchiment, sinon le fond sombre se compte comme
// de l'encre.
export function encreParCote(cv: HTMLCanvasElement): { g: number; d: number; h: number; b: number } {
  const w = cv.width, h = cv.height;
  const vide = { g: 0, d: 0, h: 0, b: 0 };
  if (w < 30 || h < 30) return vide;
  const L = luminances(cv);
  const tri = Array.from(L).sort((a, b) => a - b);
  // Seuil de NOIR FRANC : un caractère imprimé est très sombre, alors qu'une
  // frange de bord est grise. Un seuil proportionnel à la médiane comptait ces
  // gris comme du texte et déclenchait des reprises inutiles.
  const seuil = Math.min((tri[tri.length >> 1] || 1) * 0.5, 90);
  const e = Math.max(2, Math.round(Math.min(w, h) * 0.006));
  const part = (cnt: number, prof: number, lire: (i: number, c: number) => number): number => {
    let n = 0, encre = 0;
    for (let i = 0; i < cnt; i++) for (let c = 0; c < prof; c++) { n++; if (lire(i, c) < seuil) encre++; }
    return n ? (100 * encre) / n : 0;
  };
  return {
    g: part(h, e, (y, c) => L[y * w + c]),
    d: part(h, e, (y, c) => L[y * w + (w - 1 - c)]),
    h: part(w, e, (x, c) => L[c * w + x]),
    b: part(w, e, (x, c) => L[(h - 1 - c) * w + x]),
  };
}

// Élargit UN SEUL côté du quadrilatère : quand le cadrage a mordu en bas, il
// n'y a aucune raison de reprendre aussi les trois autres côtés.
function elargirCote(quad: Quad, cote: 'g' | 'd' | 'h' | 'b', part: number): Quad {
  const q: Quad = { tl: { ...quad.tl }, tr: { ...quad.tr }, br: { ...quad.br }, bl: { ...quad.bl } };
  const dep = (a: Corner, b: Corner, cible: Corner) => {
    cible.x += (b.x - a.x) * part;
    cible.y += (b.y - a.y) * part;
  };
  if (cote === 'h') { dep(quad.bl, quad.tl, q.tl); dep(quad.br, quad.tr, q.tr); }
  if (cote === 'b') { dep(quad.tl, quad.bl, q.bl); dep(quad.tr, quad.br, q.br); }
  if (cote === 'g') { dep(quad.tr, quad.tl, q.tl); dep(quad.br, quad.bl, q.bl); }
  if (cote === 'd') { dep(quad.tl, quad.tr, q.tr); dep(quad.bl, quad.br, q.br); }
  return q;
}

// ── Redressement de l'inclinaison ──────────────────────────────

// Un document peut sortir de la mise à plat avec ses bords bien droits et son
// texte incliné : il suffit que le quadrilatère détecté ne soit pas exactement
// aligné sur le papier, et le redressement cisaille le contenu. Mesuré sur un
// scan réel : bords parfaits, texte à 4 degrés. On mesure donc l'angle des
// LIGNES DE TEXTE et on le corrige.
export function angleLignes(cv: HTMLCanvasElement): number {
  const w = cv.width, h = cv.height;
  if (w < 80 || h < 80) return 0;
  const L = luminances(cv);
  const tri = Array.from(L).sort((a, b) => a - b);
  const med = tri[tri.length >> 1] || 1;
  const K = 7, D = Math.min(60, Math.max(10, Math.round(h * 0.02)));
  const centres: number[] = [];
  const bandes: Float32Array[] = [];
  for (let k = 0; k < K; k++) {
    const x0 = Math.round((k * (w - w / K)) / (K - 1)), x1 = Math.min(w, x0 + Math.round(w / K));
    const p = new Float32Array(h);
    for (let y = 0; y < h; y++) {
      let a = 0;
      for (let x = x0; x < x1; x += 2) { const v = med - L[y * w + x]; if (v > 0) a += v; }
      p[y] = a;
    }
    bandes.push(p);
    centres.push((x0 + x1) / 2);
  }
  const ref = bandes[K >> 1];
  let nRef = 0;
  for (let y = 0; y < h; y++) nRef += ref[y] * ref[y];
  nRef = Math.sqrt(nRef);
  if (nRef < 1e-6) return 0;
  const xs: number[] = [], ys: number[] = [];
  for (let k = 0; k < K; k++) {
    let nb = 0;
    for (let y = 0; y < h; y++) nb += bandes[k][y] * bandes[k][y];
    nb = Math.sqrt(nb);
    if (nb < 1e-6) continue;
    let best = -2, bs = 0;
    for (let sh = -D; sh <= D; sh++) {
      let acc = 0;
      for (let y = Math.max(0, -sh); y < Math.min(h, h - sh); y++) acc += ref[y] * bandes[k][y + sh];
      const v = acc / (nRef * nb);
      if (v > best) { best = v; bs = sh; }
    }
    if (best > 0.5) { xs.push(centres[k]); ys.push(bs); }
  }
  if (xs.length < 3) return 0;
  // Régression linéaire : la pente du décalage selon la position horizontale
  // donne directement l'inclinaison.
  const n = xs.length;
  const sx = xs.reduce((a, b) => a + b, 0), sy = ys.reduce((a, b) => a + b, 0);
  const sxx = xs.reduce((a, b) => a + b * b, 0);
  let sxy = 0;
  for (let i = 0; i < n; i++) sxy += xs[i] * ys[i];
  const den = n * sxx - sx * sx;
  if (Math.abs(den) < 1e-9) return 0;
  const pente = (n * sxy - sx * sy) / den;
  const ord = (sy - pente * sx) / n;
  // Coefficient de détermination : une vraie inclinaison donne des décalages
  // parfaitement alignés sur une droite. Un document en colonnes donne des
  // décalages dispersés, qu'une régression prendrait à tort pour une pente et
  // qui feraient tourner l'image de travers (constaté en recette).
  const moy = sy / n;
  let sr = 0, st = 0;
  for (let i = 0; i < n; i++) {
    const p = pente * xs[i] + ord;
    sr += (ys[i] - p) * (ys[i] - p);
    st += (ys[i] - moy) * (ys[i] - moy);
  }
  const r2 = st > 1e-9 ? 1 - sr / st : 0;
  if (n < 5 || r2 < 0.9) return 0;
  return (Math.atan(pente) * 180) / Math.PI;
}

export function tournerCanvas(cv: HTMLCanvasElement, deg: number): HTMLCanvasElement {
  const a = (deg * Math.PI) / 180;
  const w = cv.width, h = cv.height;
  const ca = Math.abs(Math.cos(a)), sa = Math.abs(Math.sin(a));
  const nw = Math.ceil(w * ca + h * sa), nh = Math.ceil(w * sa + h * ca);
  const out = document.createElement('canvas');
  out.width = nw; out.height = nh;
  const ctx = ctx2d(out);
  ctx.fillStyle = '#ffffff';          // les coins découverts sont du papier, pas du noir
  ctx.fillRect(0, 0, nw, nh);
  ctx.translate(nw / 2, nh / 2);
  ctx.rotate(a);
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(cv, -w / 2, -h / 2);
  return out;
}

// Deux passes : la première mesure est approximative sur un document très
// incliné, la seconde finit le travail (mesuré : 4,4 deg puis 0,7 deg, reste 0,1).
export function redresserInclinaison(cv: HTMLCanvasElement): { canvas: HTMLCanvasElement; angle: number } {
  let courant = cv, total = 0;
  for (let passe = 0; passe < 2; passe++) {
    const a = angleLignes(courant);
    // Au-delà de 12 degrés, ce n'est plus une inclinaison résiduelle mais une
    // mesure qui a décroché : on n'y touche pas.
    if (!isFinite(a) || Math.abs(a) < 0.4 || Math.abs(a) > 12) break;
    courant = tournerCanvas(courant, a);
    total += a;
  }
  return { canvas: courant, angle: Math.round(total * 100) / 100 };
}

// ── Coupe droite du liseré de fond ─────────────────────────────

// Même bien placé, un contour laisse un liseré de quelques pixels : le bord du
// papier n'est jamais net sur une photo. On le retire d'UN SEUL trait par côté :
// découper ligne par ligne laisserait un bord en escalier, visible comme une
// dentelure sur le document rangé.
export function couperLisere(canvas: HTMLCanvasElement): HTMLCanvasElement {
  const w = canvas.width, h = canvas.height;
  if (w < 40 || h < 40) return canvas;
  const L = luminances(canvas);
  const centre: number[] = [];
  for (let y = (h * 0.25) | 0; y < h * 0.75; y += 3) {
    for (let x = (w * 0.25) | 0; x < w * 0.75; x += 3) centre.push(L[y * w + x]);
  }
  if (!centre.length) return canvas;
  const profMax = Math.max(4, Math.round(Math.min(w, h) * 0.05));

  const papierLigne = (cnt: number, ech: (i: number) => number[]): number[] => {
    const out: number[] = [];
    for (let i = 0; i < cnt; i++) out.push(medianeDe(ech(i)));
    return out;
  };
  const papL = papierLigne(h, (y) => {
    const v: number[] = [];
    for (let x = (w * 0.3) | 0; x < w * 0.7; x += 2) v.push(L[y * w + x]);
    return v;
  });
  const papC = papierLigne(w, (x) => {
    const v: number[] = [];
    for (let y = (h * 0.3) | 0; y < h * 0.7; y += 2) v.push(L[y * w + x]);
    return v;
  });

  const mesurer = (cnt: number, lire: (i: number, c: number) => number, pap: number[]): number[] => {
    const brut: number[] = [];
    for (let i = 0; i < cnt; i++) {
      const seuil = Math.max(20, pap[i] * 0.78);
      let c = 0;
      while (c < profMax) { const v = lire(i, c); if (v < seuil || v > 254.5) c++; else break; }
      brut.push(c >= profMax ? 0 : c);
    }
    // Médiane glissante : une lettre qui touche le bord est isolée sur quelques
    // lignes et se trouve écartée ; un liseré court sur tout le côté.
    const out: number[] = [];
    const F = 12;
    for (let i = 0; i < cnt; i++) {
      const v: number[] = [];
      for (let k = -F; k <= F; k++) { const j = i + k; if (j >= 0 && j < cnt) v.push(brut[j]); }
      let m = medianeDe(v);
      if (i < F || i >= cnt - F) m = Math.max(m, brut[i]);   // dans un coin, le fond est plus épais
      out.push(m);
    }
    return out;
  };
  const cotes = {
    g: mesurer(h, (y, c) => L[y * w + c], papL),
    d: mesurer(h, (y, c) => L[y * w + (w - 1 - c)], papL),
    h: mesurer(w, (x, c) => L[c * w + x], papC),
    b: mesurer(w, (x, c) => L[(h - 1 - c) * w + x], papC),
  };
  const p92 = (a: number[]): number => {
    const v = [...a].sort((x, y) => x - y);
    return v[Math.min(v.length - 1, Math.round(0.92 * (v.length - 1)))];
  };
  const rg = Math.min(profMax, p92(cotes.g)), rd = Math.min(profMax, p92(cotes.d));
  const rh = Math.min(profMax, p92(cotes.h)), rb = Math.min(profMax, p92(cotes.b));

  const nw = Math.max(20, w - rg - rd), nh = Math.max(20, h - rh - rb);
  const coupe = document.createElement('canvas');
  coupe.width = nw; coupe.height = nh;
  const cctx = ctx2d(coupe);
  cctx.drawImage(canvas, rg, rh, nw, nh, 0, 0, nw, nh);

  // Là où le fond allait plus loin que le retrait commun, on efface la
  // différence en fondu, pour ne pas recréer de marche.
  const img = cctx.getImageData(0, 0, nw, nh);
  const d2 = img.data;
  const fondu = (q: number, poids: number) => {
    const j = q * 4;
    for (let k = 0; k < 3; k++) d2[j + k] = d2[j + k] * (1 - poids) + 255 * poids;
  };
  for (let y = 0; y < nh; y++) {
    const restG = Math.min(profMax, cotes.g[y + rh] - rg + 1);
    for (let c = 0; c < restG; c++) fondu(y * nw + c, c < restG - 1 ? 1 : 0.75);
    const restD = Math.min(profMax, cotes.d[y + rh] - rd + 1);
    for (let c = 0; c < restD; c++) fondu(y * nw + (nw - 1 - c), c < restD - 1 ? 1 : 0.75);
  }
  for (let x = 0; x < nw; x++) {
    const restH = Math.min(profMax, cotes.h[x + rg] - rh + 1);
    for (let c = 0; c < restH; c++) fondu(c * nw + x, c < restH - 1 ? 1 : 0.75);
    const restB = Math.min(profMax, cotes.b[x + rg] - rb + 1);
    for (let c = 0; c < restB; c++) fondu((nh - 1 - c) * nw + x, c < restB - 1 ? 1 : 0.75);
  }
  cctx.putImageData(img, 0, 0);
  return coupe;
}

// ── Blanchiment ────────────────────────────────────────────────

function filtreMax(L: Float32Array, w: number, h: number, r: number): Float32Array {
  const t = new Float32Array(w * h), o = new Float32Array(w * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    let m = 0;
    for (let k = -r; k <= r; k++) { const xx = x + k; if (xx >= 0 && xx < w) { const v = L[y * w + xx]; if (v > m) m = v; } }
    t[y * w + x] = m;
  }
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    let m = 0;
    for (let k = -r; k <= r; k++) { const yy = y + k; if (yy >= 0 && yy < h) { const v = t[yy * w + x]; if (v > m) m = v; } }
    o[y * w + x] = m;
  }
  return o;
}

function flouBoite(S: Float32Array, w: number, h: number, r: number): Float32Array {
  const t = new Float32Array(w * h), o = new Float32Array(w * h);
  const d = 2 * r + 1;
  for (let y = 0; y < h; y++) {
    let s = 0;
    for (let k = -r; k <= r; k++) s += S[y * w + Math.min(w - 1, Math.max(0, k))];
    for (let x = 0; x < w; x++) {
      t[y * w + x] = s / d;
      s += S[y * w + Math.min(w - 1, x + r + 1)] - S[y * w + Math.min(w - 1, Math.max(0, x - r))];
    }
  }
  for (let x = 0; x < w; x++) {
    let s = 0;
    for (let k = -r; k <= r; k++) s += t[Math.min(h - 1, Math.max(0, k)) * w + x];
    for (let y = 0; y < h; y++) {
      o[y * w + x] = s / d;
      s += t[Math.min(h - 1, y + r + 1) * w + x] - t[Math.min(h - 1, Math.max(0, y - r)) * w + x];
    }
  }
  return o;
}

// Ramène le papier au blanc SANS pâlir l'encre. On ne multiplie pas la
// luminance : un gain éclaircit aussi le texte et fait perdre les trois quarts
// des pixels noirs sur un ticket thermique (mesuré). On raisonne en rapport au
// papier local : ce qui vaut le papier devient blanc, ce qui est nettement plus
// sombre devient franchement noir, l'entre-deux garde ses nuances.
export function blanchir(canvas: HTMLCanvasElement, force: 0 | 1 | 2 = 1): HTMLCanvasElement {
  if (!force) return canvas;
  const w = canvas.width, h = canvas.height;
  const ctx = ctx2d(canvas);
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data, n = w * h;
  const L = new Float32Array(n);
  for (let i = 0, j = 0; i < n; i++, j += 4) L[i] = 0.299 * d[j] + 0.587 * d[j + 1] + 0.114 * d[j + 2];

  const maxDim = Math.max(w, h);
  const r1 = Math.min(14, Math.max(3, Math.round(maxDim / 220)));
  const r2 = Math.min(90, Math.max(12, Math.round(maxDim / 26)));
  const fond = flouBoite(filtreMax(L, w, h, r1), w, h, r2);

  const noirRel = force === 2 ? 0.45 : 0.34;
  const blancRel = force === 2 ? 0.84 : 0.90;
  const etendue = blancRel - noirRel;
  const borne = (v: number) => (v < 0 ? 0 : v > 255 ? 255 : v);

  for (let i = 0, j = 0; i < n; i++, j += 4) {
    const base = Math.max(12, fond[i]);
    let t = (L[i] / base - noirRel) / etendue;
    t = t < 0 ? 0 : t > 1 ? 1 : t;
    const k = Math.min(3, (255 * t) / Math.max(6, L[i]));
    d[j] = borne(d[j] * k);
    d[j + 1] = borne(d[j + 1] * k);
    d[j + 2] = borne(d[j + 2] * k);
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

// ── Diagnostic d'une page pour l'utilisateur ───────────────────

export interface DiagnosticPage {
  angle: number;      // inclinaison des lignes de texte, en degrés
  fond: number;       // part de fond resté au ras des bords, en %
  soucis: string[];   // formulé pour être lu tel quel par le salarié
}

// Ce qui mérite qu'on signale une page à l'utilisateur. On ne le dérange que
// pour ce qui se voit : un texte penché, ou du décor resté sur un bord.
export function diagnostiquerPage(cv: HTMLCanvasElement): DiagnosticPage {
  const angle = angleLignes(cv);
  const w = cv.width, h = cv.height;
  const L = luminances(cv);
  const tri = Array.from(L).sort((a, b) => a - b);
  const papier = tri[Math.round(0.7 * (tri.length - 1))] || 1;
  const e = Math.max(2, Math.round(Math.min(w, h) * 0.01));
  let n = 0, sombre = 0;
  for (let y = 0; y < h; y++) {
    const dedans = y >= e && y < h - e;
    for (let x = 0; x < w; x++) {
      if (dedans && x >= e && x < w - e) continue;
      n++;
      if (L[y * w + x] < papier * 0.55) sombre++;
    }
  }
  const fond = n ? (100 * sombre) / n : 0;
  const soucis: string[] = [];
  if (Math.abs(angle) >= 1) soucis.push('texte penché de ' + Math.abs(angle).toFixed(1) + '°');
  if (fond >= 8) soucis.push('du fond est resté sur un bord');
  return { angle: Math.round(angle * 100) / 100, fond: Math.round(fond * 10) / 10, soucis };
}

// ── Chaîne complète ────────────────────────────────────────────

export interface ResultatMiseAPlat {
  canvas: HTMLCanvasElement;
  // Image redressée et détourée mais NON blanchie : source de la retouche.
  // Mesurer une inclinaison sur une image blanchie est impossible, les niveaux
  // sont saturés (vérifié : -4,4 deg avant, 0 après).
  source: HTMLCanvasElement;
  methode: 'uvdoc' | 'perspective';
  ondulation: number;
  inclinaison?: number;
  ms: number;
}

// Prend l'image de travail et le quadrilatère retenu (détection ou réglage
// manuel), et rend le document mis à plat, détouré et blanchi.
// `rendreParPerspective` est fourni par l'appelant : c'est le redressement à
// quatre coins existant, qui sert de repli et de point de comparaison.
export async function mettreAPlat(
  img: HTMLImageElement | HTMLCanvasElement,
  quad: Quad,
  rendreParPerspective: () => HTMLCanvasElement | null,
  options?: { blanchiment?: 0 | 1 | 2; attenteMaxMs?: number; sansReprise?: boolean },
): Promise<ResultatMiseAPlat | null> {
  const t0 = performance.now();
  const perspective = rendreParPerspective();
  let choisi = perspective;
  let methode: 'uvdoc' | 'perspective' = 'perspective';
  let ondulation = perspective ? ondulationLignes(perspective) : 0;

  try {
    const vue = cadrerPour(img, quad, 0.88);
    if (vue) {
      // Le modèle pèse 30 Mo : en 4G lente il peut ne pas être arrivé quand le
      // salarié valide. On ne le fait jamais attendre plus de quelques secondes,
      // on rend le document redressé simplement plutôt qu'un écran figé.
      const attente = options?.attenteMaxMs ?? 6000;
      const pret = await Promise.race([
        chargerUVDoc().then(() => true).catch(() => false),
        new Promise<boolean>((r) => setTimeout(() => r(false), attente)),
      ]);
      const uv = pret ? await aplatirUVDoc(vue) : null;
      if (uv) {
        // Le modèle est excellent sur une photo de document, mais il n'est pas
        // fait pour tout (capture d'écran, montage). On compare sur l'ondulation
        // et non sur le fond au bord : un rendu qui rogne laisse toujours moins
        // de fond, ce qui ferait gagner le mauvais candidat à tous les coups.
        const tUv = ondulationLignes(uv.canvas);
        const nUv = tUv + 0.4 * fondAuPourtour(uv.canvas);
        const nPer = perspective ? ondulation + 0.4 * fondAuPourtour(perspective) : Infinity;
        if (nUv <= nPer) { choisi = uv.canvas; methode = 'uvdoc'; ondulation = tUv; }
      }
    }
  } catch {
    // Modèle indisponible (réseau, WebGPU absent) : on garde la perspective.
  }

  if (!choisi) return null;
  const droit = redresserInclinaison(choisi);
  const source = couperLisere(droit.canvas);
  const pourBlanchir = document.createElement('canvas');
  pourBlanchir.width = source.width; pourBlanchir.height = source.height;
  ctx2d(pourBlanchir).drawImage(source, 0, 0);
  const fini = blanchir(pourBlanchir, options?.blanchiment ?? 1);

  // Contrôle : a-t-on mangé du texte ? Sur un document entier, les bords sont du
  // papier vierge. Si de l'encre touche UN bord, le cadrage a mordu de ce
  // côté-là (constaté sur un document clair posé sur un fond clair, où le bord
  // bas emporte les mentions légales). On reprend ce seul côté, et on ne garde
  // la reprise que si elle améliore sans abîmer les autres bords.
  if (!options?.sansReprise) {
    const av = encreParCote(fini);
    const cotes: Array<'g' | 'd' | 'h' | 'b'> = ['g', 'd', 'h', 'b'];
    const pire = cotes.reduce((a, c) => (av[c] > av[a] ? c : a), 'g');
    if (av[pire] > 12) {
      try {
        const large = await mettreAPlat(img, elargirCote(quad, pire, 0.06), rendreParPerspective, { ...options, sansReprise: true });
        if (large) {
          const ap = encreParCote(large.canvas);
          // Bilan net : ce qu'on récupère sur le bord coupé doit dépasser ce
          // qu'on ramène de décor sur les trois autres. Exiger qu'aucun autre
          // bord ne bouge écartait des reprises qui amélioraient nettement.
          const gain = av[pire] - ap[pire];
          const perte = cotes.reduce((t, c) => (c === pire ? t : t + Math.max(0, ap[c] - av[c])), 0);
          if (gain > perte + 2) return { ...large, ms: Math.round(performance.now() - t0) };
        }
      } catch { /* la reprise a échoué : on garde le premier rendu */ }
    }
  }
  return { canvas: fini, source, methode, ondulation, inclinaison: droit.angle, ms: Math.round(performance.now() - t0) };
}
